import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, TextField, Button, Checkbox, FormControlLabel, Alert, Stack, Container } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { useVerifyOTP } from 'packages/workspace/services/hooks/useAccount';
import { useRouter } from 'next/router';
import { uiStore } from 'packages/workspace/services/store/ui-store';
import { TimeOutConfigs } from 'packages/workspace/services/config';
import { signOut } from '@aws-amplify/auth';
import { AbilityStore } from 'packages/workspace/services/store/abilities-store';
import { sessionStore } from 'packages/workspace/services/store/session-store';

export interface DeviceComponentProps {
  setDeviceVerified: (isVerified: boolean) => void;
}

export default function DeviceVerification(props: DeviceComponentProps) {
  const { setDeviceVerified } = props;
  const [verificationCode, setVerificationCode] = useState('');
  const account = useVerifyOTP();
  const router = useRouter();

  const handleSignOut = async () => {
    try {
      await signOut();
      AbilityStore.clearRules();
      sessionStore.unsetDiscoveryParams();
      router.push('/auth/auth.page');
    } catch (error) {
      uiStore.setErrorMessage('error signing out: ', error);
    }
  };

  const handleVerify = async () => {
    account.mutate(
      { otp: verificationCode },
      {
        onSuccess(data) {
          if (data?.data?.otpExpired) {
            uiStore.setErrorMessage("OTP has expired. Please try again.");
            handleSignOut();
          } else if (!data?.data?.otpValid) {
            uiStore.setErrorMessage("Invalid OTP entered");
          } else {
            uiStore.setSuccessMessage(data?.data?.message || "OTP verified successfully");
            setTimeout(() => {
              setDeviceVerified(true);
            }, TimeOutConfigs.MINIMUM_INTERVAL);
          }
        },
        onError(error) {
          const errorMessage = error?.errors[0] || 'Verification failed. Please try again.'
          uiStore.setErrorMessage(errorMessage);
        }
      }
    );
  };

  const handleCancel = () => {
    handleSignOut();
  };

  return (
    <Container maxWidth={false} disableGutters={true}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
          py: 4,
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 480,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Header with Icon */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  bgcolor: '#fff3cd',
                  borderRadius: '8px',
                  mb: 2,
                }}
              >
                <SecurityIcon sx={{ color: '#f57c00', fontSize: 28 }} />
              </Box>
              <Typography variant="h5" component="h1" fontWeight={600} gutterBottom>
                Device Verification Required
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We've detected a login from a new device or location
              </Typography>
            </Box>

            {/* Info Alert */}
            <Alert
              severity="info"
              sx={{
                mb: 3,
                bgcolor: '#e3f2fd',
                '& .MuiAlert-icon': {
                  color: '#1976d2',
                },
              }}
            >
              For your security, please verify your identity with the code sent to your email address.
            </Alert>

            {/* Form */}
            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Enter Verification Code
                </Typography>
                <TextField
                  fullWidth
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="000000"
                  variant="outlined"
                  inputProps={{
                    style: {
                      textAlign: 'center',
                      fontSize: '18px',
                      letterSpacing: '4px',
                      fontFamily: 'monospace',
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Enter the 6-digit code from your email
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCancel}
                  sx={{
                    py: 1.5,
                    borderColor: '#d1d5db',
                    color: '#6b7280',
                    '&:hover': {
                      borderColor: '#9ca3af',
                      bgcolor: '#f9fafb',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleVerify}
                  sx={{
                    py: 1.5,
                    bgcolor: '#1976d2',
                    '&:hover': {
                      bgcolor: '#1565c0',
                    },
                  }}
                >
                  Verify Device
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
